import { fetchStudentPlan } from './studentPlan';
import type { FocusByDomainContent } from './domainFocus';
import type { PlanSnapshotContent } from './planSnapshotContent';

export type SavedPlanInputs = {
  focusByDomain?: Record<string, unknown>;
  accommodations?: unknown;
  planSnapshot?: unknown;
};

export type SavedPlanBody = {
  focusByDomain?: Record<string, FocusByDomainContent>;
  accommodations?: string[] | null;
  suggestedItems?: string[] | null;
  planSnapshot?: PlanSnapshotContent | null;
  inputs?: SavedPlanInputs;
};

function stableStringify(value: unknown): string {
  if (value === null || typeof value !== 'object') return JSON.stringify(value) ?? 'null';
  if (Array.isArray(value)) return `[${value.map(stableStringify).join(',')}]`;
  const entries = Object.keys(value as Record<string, unknown>)
    .sort()
    .map((key) => `${JSON.stringify(key)}:${stableStringify((value as Record<string, unknown>)[key])}`);
  return `{${entries.join(',')}}`;
}

export function sameInputs(a: unknown, b: unknown): boolean {
  if (a == null || b == null) return false;
  return stableStringify(a) === stableStringify(b);
}

let pending: { studentId: string; body: Promise<SavedPlanBody | null> } | null = null;

export function resetPlanCache(): void {
  pending = null;
}

export function seedSavedPlan(studentId: string, body: SavedPlanBody): void {
  pending = { studentId, body: Promise.resolve(body) };
}

function loadSavedPlan(studentId: string): Promise<SavedPlanBody | null> {
  if (pending == null || pending.studentId !== studentId) {
    pending = {
      studentId,
      body: fetchStudentPlan(studentId)
        .then((record) => ((record?.plan as SavedPlanBody | undefined) ?? null))
        .catch(() => null),
    };
  }
  return pending.body;
}

export async function cachedFocusByDomain(
  studentId: string,
  domain: string,
  inputs: unknown,
): Promise<FocusByDomainContent | null> {
  const plan = await loadSavedPlan(studentId);
  const content = plan?.focusByDomain?.[domain];
  if (content == null || !content.recommendation) return null;
  return sameInputs(plan?.inputs?.focusByDomain?.[domain], inputs) ? content : null;
}

export async function cachedAccommodations(
  studentId: string,
  inputs: unknown,
): Promise<string[] | null> {
  const plan = await loadSavedPlan(studentId);
  const items = plan?.accommodations;
  if (!Array.isArray(items) || items.length === 0) return null;
  return sameInputs(plan?.inputs?.accommodations, inputs) ? items : null;
}

export async function cachedSuggestedItems(
  studentId: string,
  inputs: unknown,
): Promise<string[] | null> {
  const plan = await loadSavedPlan(studentId);
  const ids = plan?.suggestedItems;
  if (!Array.isArray(ids)) return null;
  return sameInputs(plan?.inputs?.accommodations, inputs) ? ids.filter((id): id is string => typeof id === 'string') : null;
}

export async function cachedPlanSnapshot(
  studentId: string,
  inputs: unknown,
): Promise<PlanSnapshotContent | null> {
  const plan = await loadSavedPlan(studentId);
  const content = plan?.planSnapshot;
  if (content == null || content.capabilities.length !== 4) return null;
  return sameInputs(plan?.inputs?.planSnapshot, inputs) ? content : null;
}
